import { api, type ApiResponse } from './client';

// ================== TYPE DEFINITIONS ==================

// Base User Interface
export interface User {
  id?: number;
  uuid: string;
  email: string;
  full_name: string;
  email_verified_at?: string;
  phone?: string;
  job_title?: string;
  department?: string;
  location?: string;
  user_role?: {
    uuid: string;
    name: string;
  };
  user_status?: {
    uuid: string;
    name: string;
  };
  [key: string]: any;
}

// General user profile update (for any user type - employee, employer, etc.)
export interface UserProfileUpdateData {
  full_name?: string;
  phone?: string;
  job_title?: string;
  department?: string;
  location?: string;
}

// Charity-specific profile update (User fields only)
export interface CharityUserProfileUpdateData {
  full_name?: string;
  phone?: string;
  job_title?: string;
  department?: string;
  location?: string;
}

// Charity organization update (for Charity table fields)
export interface CharityOrganizationUpdateData {
  name?: string;
  abn?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  head_office_location?: string;
  charity_government_id?: string;
  category_uuid?: string;
  dgr_tax_status?: string;
  description?: string;
  summary?: string;
  extended_description?: string;
  /** BSB for payouts (stored as verified_bsb_no in API) */
  verified_bsb_no?: string | null;
  verified_acc_no?: string | null;
  verified_acc_name?: string | null;
}

// Employer organization update (for Employer table fields)
export interface EmployerOrganizationUpdateData {
  name?: string;
  abn?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  head_office_location?: string;
}

// Combined charity update (both user and organization)
export interface CharityCompleteUpdateData {
  userProfile?: CharityUserProfileUpdateData;
  organizationProfile?: CharityOrganizationUpdateData;
}

export interface UserUpdateResponse {
  success: boolean;
  user?: User;
  error?: any;
  message?: string;
}

export interface CharityUpdateResponse {
  success: boolean;
  user?: User;
  charity?: any;
  error?: any;
  message?: string;
}

export interface EmployerUpdateResponse {
  success: boolean;
  user?: User;
  employer?: any;
  error?: any;
  message?: string;
}

// ================== USER SERVICE CLASS ==================

export class UserService {
  
  /**
   * Decode JWT token to extract payload
   */
  static decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  /**
   * Get user UUID from JWT token
   */
  static getUserUuidFromToken(): string | null {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      const payload = this.decodeJWT(token);
      return payload?.uuid || payload?.user_uuid || payload?.sub || null;
    } catch {
      return null;
    }
  }
  
  /**
   * Get current authenticated user from localStorage
   */
  static getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  /**
   * Update user in localStorage
   */
  static updateUserInStorage(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  /**
   * Get charity UUID from current user
   */
  static getCharityUuidFromUser(): string | null {
    try {
      const user = this.getCurrentUser();
      if (user?.organization?.type === 'charity' && user?.organization?.data?.uuid) {
        return user.organization.data.uuid;
      }
      return null;
    } catch (error) {
      console.error('Error getting charity UUID:', error);
      return null;
    }
  }

  /**
   * Update user profile (general method for any user type)
   */
  static async updateUserProfile(profileData: UserProfileUpdateData): Promise<UserUpdateResponse> {
    try {
      // Get user UUID from the current user object instead of token
      const currentUser = this.getCurrentUser();
      if (!currentUser?.uuid) {
        return { success: false, error: 'User not authenticated' };
      }

      const response = await api.put<ApiResponse<User>>(`/user/${currentUser.uuid}`, profileData);
      const updatedUser = response.data.data;
      
      this.updateUserInStorage(updatedUser);
      
      return { 
        success: true, 
        user: updatedUser,
        message: 'Profile updated successfully'
      };
    } catch (error: any) {
      return { 
        success: false, 
        error,
        message: 'Failed to update profile'
      };
    }
  }

  /**
   * Update charity organization profile (charity details)
   */
  static async updateCharityOrganization(organizationData: CharityOrganizationUpdateData): Promise<CharityUpdateResponse> {
    try {
      // Get the charity UUID associated with this user from localStorage
      const charityUuid = this.getCharityUuidFromUser();
      if (!charityUuid) {
        return { success: false, error: 'Charity not found for user' };
      }

      const response = await api.put<ApiResponse<any>>(`/charity/${charityUuid}`, organizationData);
      const body = response.data as { data?: any; message?: string };
      return { 
        success: true, 
        charity: body.data,
        message: body.message || 'Charity organization updated successfully'
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.message || 'Failed to update charity organization';
      return { 
        success: false, 
        error,
        message
      };
    }
  }

  /**
   * Update charity logo.
   */
  static async updateCharityLogo(logo: File): Promise<CharityUpdateResponse> {
    try {
      const charityUuid = this.getCharityUuidFromUser();
      if (!charityUuid) {
        return { success: false, error: 'Charity not found for user' };
      }

      const formData = new FormData();
      formData.append('logo', logo);

      const response = await api.post<ApiResponse<any>>(`/charity/${charityUuid}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const body = response.data as { success?: boolean; data?: any; message?: string };
      if (body.success === false) {
        return {
          success: false,
          error: body,
          message: body.message || 'Failed to update charity logo',
        };
      }
      return {
        success: true,
        charity: body.data,
        message: body.message || 'Charity logo updated successfully'
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.message || 'Failed to update charity logo';
      return {
        success: false,
        error,
        message
      };
    }
  }



  // ================== EMPLOYER METHODS ==================

  /**
   * Get employer UUID from current user
   */
  static getEmployerUuidFromUser(): string | null {
    try {
      const user = this.getCurrentUser();
      if (user?.organization?.type === 'employer' && user?.organization?.data?.uuid) {
        return user.organization.data.uuid;
      }
      return null;
    } catch (error) {
      console.error('Error getting employer UUID:', error);
      return null;
    }
      }

  /**
   * Update employer organization profile (employer details)
   */
  static async updateEmployerOrganization(organizationData: EmployerOrganizationUpdateData): Promise<EmployerUpdateResponse> {
    try {
      const employerUuid = this.getEmployerUuidFromUser();
      if (!employerUuid) {
        return { success: false, error: 'No employer found for current user' };
      }

      const response = await api.put<ApiResponse<any>>(`/employer/${employerUuid}`, organizationData);
      const updatedEmployer = response.data.data;

      return { 
        success: true, 
        employer: updatedEmployer,
        message: 'Employer organization updated successfully'
      };
    } catch (error: any) {
      return { 
        success: false, 
        error,
        message: 'Failed to update employer organization'
      };
    }
  }



} 